import {
  FieldErrors,
  Resolver,
  useController,
  useForm,
} from 'react-hook-form-v7';
import { useCreateTemplatingItem } from './useCreateTemplatingItem';
import {
  Folder,
  ModelUtils,
  TemplatingItem,
  TemplatingItemDetail,
  TemplatingItemSummary,
} from '../models';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useTemplatingItemTypeDisplay } from './useTemplatingItemTypeDisplay';
import { KnownImpressionTypes } from '../../SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';

const messages = defineMessages({
  folderNameRequired: {
    id: 'useCreateTemplatingItemForm.folderNameRequired',
    defaultMessage: 'Folder name is required',
  },
  templatingItemNameRequired: {
    id: 'useCreateTemplatingItemForm.templatingItemNameRequired',
    defaultMessage: '{typeName} name is required',
  },
  templatingItemNameMaxLength: {
    id: 'useCreateTemplatingItemForm.templatingItemNameMaxLength',
    defaultMessage: '{typeName} name cannot exceed 256 characters',
  },
  templatingItemNameInvalidCharacters: {
    id: 'useCreateTemplatingItemForm.templatingItemNameInvalidCharacters',
    defaultMessage:
      '{typeName} name contains invalid characters. Only letters, numbers, spaces, or special characters ! _ . * ( ) - are allowed.',
  },
});

type FormValues = {
  templatingItemType: TemplatingItem['type'];
  templatingItemName: string;
  shouldCreateFolder: boolean;
  folderName: string;
  description: string;
  impressionType: KnownImpressionTypes;
};

const useResolver = (): Resolver<FormValues> => {
  const intl = useIntl();
  const validateTemplatingItemName = useValidateTemplatingItemName();

  return useCallback(
    (data: FormValues) => {
      // TODO: check for existing names

      const errors: FieldErrors<FormValues> = {};

      if (data.shouldCreateFolder && data.folderName === '') {
        errors.folderName = {
          type: 'required',
          message: intl.formatMessage(messages.folderNameRequired),
        };
      }

      validateTemplatingItemName(
        data.templatingItemType,
        data.templatingItemName,
        errors,
      );

      return {
        values: data,
        errors,
      };
    },
    [intl, validateTemplatingItemName],
  );
};

export const useCreateTemplatingItemForm = ({
  folder,
  templatingItem,
  onSuccess,
}: {
  folder?: Folder;
  templatingItem?: TemplatingItemSummary;
  onSuccess?: (templatingItem: TemplatingItemDetail) => void;
}) => {
  const resolver = useResolver();

  const defaultValues = useMemo(
    () => ({
      templatingItemType: templatingItem?.type || 'template',
      templatingItemName: templatingItem
        ? `${ModelUtils.getName(templatingItem)} (copy)`
        : '',
      shouldCreateFolder: false,
      folderName: '',
      description: '',
      impressionType: 'general' as KnownImpressionTypes,
    }),
    [templatingItem],
  );

  const {
    handleSubmit,
    control,
    formState,
    reset: resetForm,
  } = useForm<FormValues>({
    defaultValues,
    resolver,
    mode: 'onChange',
  });

  // Reset form with default values when templating item changes
  useEffect(() => {
    resetForm(defaultValues);
  }, [templatingItem, resetForm, defaultValues]);

  const handleSuccess = useCallback(
    (templatingItem: TemplatingItemDetail) => {
      resetForm(defaultValues);
      onSuccess?.(templatingItem);
    },
    [resetForm, onSuccess, defaultValues],
  );

  const { createTemplatingItem, reset: resetCreateTemplatingItem } =
    useCreateTemplatingItem({
      onSuccess: handleSuccess,
    });

  const templatingItemTypeControl = useController({
    control,
    name: 'templatingItemType',
  });

  const templatingItemNameControl = useController({
    control,
    name: 'templatingItemName',
  });

  const shouldCreateFolderControl = useController({
    control,
    name: 'shouldCreateFolder',
  });

  const folderNameControl = useController({
    control,
    name: 'folderName',
    disabled: !shouldCreateFolderControl.field.value,
  });

  const impressionTypeControl = useController({
    control,
    name: 'impressionType',
  });

  const onSubmit = handleSubmit(data => {
    createTemplatingItem({
      type: data.templatingItemType,
      name: data.templatingItemName,
      folderName: data.shouldCreateFolder ? data.folderName : undefined,
      parentFolder: folder,
      impressionType: data.impressionType,
    });
  });

  const hasError = !formState.isValid;

  const reset = useCallback(() => {
    resetCreateTemplatingItem();
    resetForm(defaultValues);
  }, [resetCreateTemplatingItem, resetForm, defaultValues]);

  return {
    templatingItemTypeControl,
    templatingItemNameControl,
    shouldCreateFolderControl,
    folderNameControl,
    impressionTypeControl,
    onSubmit,
    hasError,
    reset,
  };
};

export function useValidateTemplatingItemName() {
  const intl = useIntl();
  const templatingItemTypeDisplay = useTemplatingItemTypeDisplay();

  return useCallback(
    (
      type: TemplatingItem['type'],
      value: string,
      errors: FieldErrors<FormValues>,
    ) => {
      const typeName = templatingItemTypeDisplay[type];

      if (value === '') {
        errors.templatingItemName = {
          type: 'required',
          message: intl.formatMessage(messages.templatingItemNameRequired, {
            typeName,
          }),
        };
      } else if (value.length > 256) {
        errors.templatingItemName = {
          type: 'maxLength',
          message: intl.formatMessage(messages.templatingItemNameMaxLength, {
            typeName,
          }),
        };
      } else if (!/^[a-zA-Z0-9!_.*'() -]+$/.test(value)) {
        errors.templatingItemName = {
          type: 'pattern',
          message: intl.formatMessage(
            messages.templatingItemNameInvalidCharacters,
            { typeName },
          ),
        };
      }
    },
    [intl, templatingItemTypeDisplay],
  );
}
